import { MenuItem } from '@mui/material';
import { IssueParams } from 'components/common/forms/EditableField/types';
import { useSites } from 'store/site';
import { useUsers } from 'components/dataProviders/withUsers';
import { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { useProcesses } from 'store/process';
import { logError } from 'setup/logger/logError';
import { EditableStandardEntityName } from 'shared/domain/fieldValue/fields';
import { UserSelectOption } from '../../../user/selectOption/userSelectOption';
import { MultilineTooltip } from '../multilineTooltip';
import { AvailableOption } from './types';

const menuSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
};

const SingleUserOption = (props: {
  item: AvailableOption;
  issueParams: IssueParams;
}): ReactElement | null => {
  const { item, issueParams, ...rest } = props;
  const intl = useIntl();
  const { getUserOnViewWithAccessLevelOnIconViewById } = useUsers();
  const { processes } = useProcesses();

  const {
    items: sites,
  } = useSites();

  const user =
    item._id &&
    getUserOnViewWithAccessLevelOnIconViewById(
      item._id,
      issueParams.userAccesses,
      sites,
      processes
    );
  if (!user) {
    logError('User not found! ', item._id);
    return null;
  }

  return (
    <UserSelectOption
      user={user}
      intl={intl}
      chipColor={item.chipColor || null}
      {...rest}
    />
  );
};

// Remember to pass the props down to the MenuItem
// https://stackoverflow.com/questions/66943324/mui-select-component-with-custom-children-item
export const SingleOption = (props: {
  value: string;
  item: AvailableOption;
  entityName: string | undefined;
  issueParams: IssueParams | undefined | null;
}): ReactElement => {
  const { item, entityName, issueParams, ...rest } = props;
  const intl = useIntl();

  // creatable option
  if (item.isCreationProposal) {
    return (
      <MenuItem sx={menuSx} key={item._id} {...rest}>
        {item.label}
      </MenuItem>
    );
  }

  if (entityName === EditableStandardEntityName.user && issueParams) {
    return (
      <SingleUserOption item={item} issueParams={issueParams} {...rest} />
    );
  }

  const content = (
    <MenuItem sx={menuSx} key={item._id} {...rest}>
      {item.label}
    </MenuItem>
  );

  if (item.tooltipMessage) {
    return (
      <MultilineTooltip
        message={item.tooltipMessage}
        intl={intl}
        footerLink={item.footerLink}
      >
        {content}
      </MultilineTooltip>
    );
  }
  return content;
};
